// Transverse Mercator by Krüger's series, ported from the Android app's TransverseMercator.kt
// (ITM) and UtmConverter.kt (UTM). Pure maths rather than proj4, so the grid references match the
// apps digit for digit.

import { toRadians } from './geodesy';

/** A projected position in metres. */
export interface EastingNorthing {
  easting: number;
  northing: number;
}

export interface TransverseMercatorParams {
  semiMajorAxis: number;
  flattening: number;
  scaleFactor: number;
  latitudeOfOrigin: number;
  centralMeridian: number;
  falseEasting: number;
  falseNorthing: number;
}

const atanh = (x: number): number => 0.5 * Math.log((1 + x) / (1 - x));

/**
 * Krüger's series to sixth order in n — sub-millimetre anywhere a national grid is used, the same
 * maths PROJ's `etmerc` runs.
 */
export class TransverseMercator {
  private readonly scaleFactor: number;
  private readonly centralMeridian: number;
  private readonly falseEasting: number;
  private readonly falseNorthing: number;
  private readonly eccentricity: number;
  private readonly rectifyingRadius: number;
  private readonly alpha: number[];
  private readonly originXi: number;

  constructor(params: TransverseMercatorParams) {
    this.scaleFactor = params.scaleFactor;
    this.centralMeridian = params.centralMeridian;
    this.falseEasting = params.falseEasting;
    this.falseNorthing = params.falseNorthing;
    const n = params.flattening / (2 - params.flattening);
    this.eccentricity = 2 * Math.sqrt(n) / (1 + n);
    const n2 = n * n;
    const n3 = n2 * n;
    const n4 = n3 * n;
    const n5 = n4 * n;
    const n6 = n5 * n;
    this.rectifyingRadius = params.semiMajorAxis / (1 + n) * (1 + n2 / 4 + n4 / 64 + n6 / 256);
    this.alpha = [
      n / 2 - 2.0 / 3 * n2 + 5.0 / 16 * n3 + 41.0 / 180 * n4 - 127.0 / 288 * n5 + 7891.0 / 37800 * n6,
      13.0 / 48 * n2 - 3.0 / 5 * n3 + 557.0 / 1440 * n4 + 281.0 / 630 * n5 - 1983433.0 / 1935360 * n6,
      61.0 / 240 * n3 - 103.0 / 140 * n4 + 15061.0 / 26880 * n5 + 167603.0 / 181440 * n6,
      49561.0 / 161280 * n4 - 179.0 / 168 * n5 + 6601661.0 / 7257600 * n6,
      34729.0 / 80640 * n5 - 3418889.0 / 1995840 * n6,
      212378941.0 / 319334400 * n6,
    ];
    // The meridian distance to the latitude of origin, in the same ξ units as the series.
    this.originXi = this.series(params.latitudeOfOrigin, 0.0)[0];
  }

  project(latitude: number, longitude: number): EastingNorthing {
    const [xi, eta] = this.series(latitude, longitude - this.centralMeridian);
    return {
      easting: this.falseEasting + this.scaleFactor * this.rectifyingRadius * eta,
      northing: this.falseNorthing + this.scaleFactor * this.rectifyingRadius * (xi - this.originXi),
    };
  }

  /** Krüger's ξ, η for a geodetic latitude and a longitude offset from the central meridian. */
  private series(latitude: number, deltaLongitude: number): [number, number] {
    const phi = toRadians(latitude);
    const lambda = toRadians(deltaLongitude);
    const t = Math.sinh(atanh(Math.sin(phi)) - this.eccentricity * atanh(this.eccentricity * Math.sin(phi)));
    const xiPrime = Math.atan(t / Math.cos(lambda));
    const etaPrime = atanh(Math.sin(lambda) / Math.sqrt(1 + t * t));
    let xi = xiPrime;
    let eta = etaPrime;
    for (let j = 1; j <= this.alpha.length; j++) {
      xi += this.alpha[j - 1] * Math.sin(2 * j * xiPrime) * Math.cosh(2 * j * etaPrime);
      eta += this.alpha[j - 1] * Math.cos(2 * j * xiPrime) * Math.sinh(2 * j * etaPrime);
    }
    return [xi, eta];
  }
}

/** Irish Transverse Mercator, EPSG:2157 (GRS80; ETRS89 is WGS84 to well under a metre). */
export const ITM = new TransverseMercator({
  semiMajorAxis: 6_378_137.0,
  flattening: 1 / 298.257222101,
  scaleFactor: 0.99982,
  latitudeOfOrigin: 53.5,
  centralMeridian: -8.0,
  falseEasting: 600_000.0,
  falseNorthing: 750_000.0,
});

/** A WGS84 position in the Universal Transverse Mercator grid. */
export interface UtmCoordinate {
  zone: number;
  band: string;
  easting: number;
  northing: number;
}

const UTM_SEMI_MAJOR_AXIS = 6_378_137.0;
const UTM_FLATTENING = 1 / 298.257223563;
const UTM_SCALE_FACTOR = 0.9996;
const UTM_FALSE_EASTING = 500_000.0;
const UTM_FALSE_NORTHING_SOUTH = 10_000_000.0;
const UTM_BAND_LETTERS = 'CDEFGHJKLMNPQRSTUVWX';

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

/** Always folds through the modulo, unlike geodesy's normalizeLongitude — as UtmConverter does. */
function utmLongitude(longitude: number): number {
  let lon = (longitude + 180.0) % 360.0;
  if (lon < 0) lon += 360.0;
  return lon - 180.0;
}

/** Zone 1–60, with the Norway and Svalbard exceptions. [longitude] must be in [-180, 180). */
export function utmZone(latitude: number, longitude: number): number {
  if (latitude >= 56.0 && latitude < 64.0 && longitude >= 3.0 && longitude < 12.0) return 32;
  if (latitude >= 72.0) {
    if (longitude >= 0.0 && longitude < 9.0) return 31;
    if (longitude >= 9.0 && longitude < 21.0) return 33;
    if (longitude >= 21.0 && longitude < 33.0) return 35;
    if (longitude >= 33.0 && longitude < 42.0) return 37;
  }
  return clamp(Math.floor((longitude + 180.0) / 6.0) + 1, 1, 60);
}

/** Latitude band C–X, 8° each; X stretches to 84°N. */
export function utmBand(latitude: number): string {
  return UTM_BAND_LETTERS[clamp(Math.floor((latitude + 80.0) / 8.0), 0, UTM_BAND_LETTERS.length - 1)];
}

/**
 * WGS84 to UTM by Krüger's series to third order in n (sub-millimetre inside a zone). Undefined
 * outside 80°S–84°N, where UPS covers the poles.
 */
export function toUtm(latitude: number, longitude: number): UtmCoordinate | null {
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  if (latitude < -80.0 || latitude > 84.0) return null;
  const lon = utmLongitude(longitude);
  const zone = utmZone(latitude, lon);
  const band = utmBand(latitude);
  const centralMeridian = toRadians((zone - 1) * 6.0 - 180.0 + 3.0);

  const n = UTM_FLATTENING / (2 - UTM_FLATTENING);
  const n2 = n * n;
  const n3 = n2 * n;
  const rectifyingRadius = UTM_SEMI_MAJOR_AXIS / (1 + n) * (1 + n2 / 4 + n2 * n2 / 64);
  const alpha = [
    n / 2 - 2.0 / 3 * n2 + 5.0 / 16 * n3,
    13.0 / 48 * n2 - 3.0 / 5 * n3,
    61.0 / 240 * n3,
  ];

  const phi = toRadians(latitude);
  const lambda = toRadians(lon) - centralMeridian;
  const k = 2 * Math.sqrt(n) / (1 + n);
  const t = Math.sinh(atanh(Math.sin(phi)) - k * atanh(k * Math.sin(phi)));
  const xiPrime = Math.atan(t / Math.cos(lambda));
  const etaPrime = atanh(Math.sin(lambda) / Math.sqrt(1 + t * t));

  let xi = xiPrime;
  let eta = etaPrime;
  for (let j = 1; j <= 3; j++) {
    xi += alpha[j - 1] * Math.sin(2 * j * xiPrime) * Math.cosh(2 * j * etaPrime);
    eta += alpha[j - 1] * Math.cos(2 * j * xiPrime) * Math.sinh(2 * j * etaPrime);
  }

  const easting = UTM_FALSE_EASTING + UTM_SCALE_FACTOR * rectifyingRadius * eta;
  const northing = UTM_SCALE_FACTOR * rectifyingRadius * xi + (latitude < 0 ? UTM_FALSE_NORTHING_SOUTH : 0.0);
  return { zone, band, easting, northing };
}

/**
 * "30U 500579 5917188". Metres are truncated, not rounded — the grid convention (a reference names
 * the square the point is in).
 */
export function formatUtm(utm: UtmCoordinate): string {
  return `${utm.zone}${utm.band} ${Math.floor(utm.easting)} ${Math.floor(utm.northing)}`;
}
